import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, FileText, Shield, CreditCard, MoreHorizontal, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { guideAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { getField } from '../lib/localize';

const InformationHub = () => {
  const [guides, setGuides] = useState([]);
  const [filteredGuides, setFilteredGuides] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  const categories = [
    { id: 'all', label: t('categories.all'), icon: MoreHorizontal },
    { id: 'documentation', label: t('categories.documentation'), icon: FileText },
    { id: 'legal', label: t('categories.legal'), icon: Shield },
    { id: 'fees', label: t('categories.fees'), icon: CreditCard },
  ];

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const res = await guideAPI.getAll();
        setGuides(res.data);
        setFilteredGuides(res.data);
      } catch (err) {
        console.error('Failed to fetch guides:', err);
      }
      setLoading(false);
    };
    fetchGuides();
  }, []);

  useEffect(() => {
    let result = guides;
    
    if (activeCategory !== 'all') {
      result = result.filter(g => g.category === activeCategory);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(g => 
        (g.title || '').toLowerCase().includes(searchLower) ||
        (g.title_en || '').toLowerCase().includes(searchLower) ||
        (g.summary || '').toLowerCase().includes(searchLower) ||
        (g.summary_en || '').toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredGuides(result);
  }, [search, activeCategory, guides]);

  const categoryIcons = {
    documentation: FileText,
    legal: Shield,
    fees: CreditCard,
  };

  const categoryLabels = {
    documentation: t('categories.documentation'),
    legal: t('categories.legal'),
    fees: t('categories.fees'),
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      <SEO 
        title={language === 'en' ? 'Land Information Guides' : 'ভূমি তথ্য গাইড'}
        description={language === 'en'
          ? 'Complete guides on land registration, Namjari, Khatian, deed documentation and more in Bangladesh.'
          : 'বাংলাদেশে জমি রেজিস্ট্রেশন, নামজারি, খতিয়ান, দলিল সম্পর্কে সম্পূর্ণ গাইড।'
        }
        path="/guides"
      />
      <Navbar isDark={isDark} />

      {/* Header */}
      <section className={`pt-24 pb-12 border-b ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="hub-title">
              {t('hub.title')}
            </h1>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('hub.subtitle')}
            </p>
          </div>

          {/* Search */}
          <div className="mt-8 max-w-xl">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder={t('hub.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-12 h-12 rounded-full ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : 'border-gray-300'} focus:border-[#2E7D32] focus:ring-[#2E7D32]`}
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Categories */}
          <div className="mt-6 flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'default' : 'outline'}
                onClick={() => setActiveCategory(cat.id)}
                className={`rounded-full ${
                  activeCategory === cat.id
                    ? 'bg-[#2E7D32] hover:bg-[#1B5E20] text-white'
                    : isDark
                      ? 'border-white/20 text-gray-300 hover:border-[#2E7D32] hover:text-[#2E7D32]'
                      : 'border-gray-300 text-gray-600 hover:border-[#2E7D32] hover:text-[#2E7D32]'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <cat.icon size={16} className="mr-2" />
                {cat.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className={`rounded-xl p-6 animate-pulse ${isDark ? 'bg-[#111]' : 'bg-white'}`}>
                  <div className={`h-6 rounded w-3/4 mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-4 rounded w-full mb-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`h-4 rounded w-2/3 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`} />
                </div>
              ))}
            </div>
          ) : filteredGuides.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('hub.noResults')}</h3>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('hub.tryAnother')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGuides.map((guide) => {
                const IconComponent = categoryIcons[guide.category] || FileText;
                return (
                  <Link
                    key={guide.id}
                    to={`/guides/${guide.slug}`}
                    className={`rounded-xl p-6 border transition-all duration-200 group ${
                      isDark 
                        ? 'bg-[#111] border-white/10 hover:border-[#2E7D32]' 
                        : 'bg-white border-gray-200 hover:border-[#2E7D32] hover:shadow-lg'
                    }`}
                    data-testid={`guide-${guide.slug}`}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center group-hover:bg-[#2E7D32] transition-colors ${isDark ? 'bg-[#2E7D32]/20' : 'bg-[#E8F5E9]'}`}>
                        <IconComponent className="text-[#2E7D32] group-hover:text-white transition-colors" size={20} />
                      </div>
                      <span className={`text-xs uppercase tracking-wider font-medium px-2 py-1 rounded ${isDark ? 'text-[#FBC02D] bg-[#FBC02D]/10' : 'text-[#2E7D32] bg-[#E8F5E9]'}`}>
                        {categoryLabels[guide.category]}
                      </span>
                    </div>
                    <h3 className={`text-lg font-semibold mb-2 group-hover:text-[#2E7D32] transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {getField(guide, 'title', language)}
                    </h3>
                    <p className={`text-sm line-clamp-2 mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {getField(guide, 'summary', language)}
                    </p>
                    <div className="flex items-center text-[#2E7D32] text-sm font-medium">
                      {t('landing.readMore')} 
                      <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer isDark={isDark} />
    </div>
  );
};

export default InformationHub;
